import i18next from "i18next";

// i18next v26 doesn't export MissingKeyHandler as a named type; inline the signature.
type MissingKeyHandler = (
  lngs: readonly string[],
  ns: string,
  key: string,
  fallbackValue: string,
  updateMissing: boolean,
  options: unknown,
) => string | undefined | void;

/**
 * Pitfall C6 / I18N-08 — missing key handling.
 *
 * A missing translation key must NEVER render as the literal key string in
 * production (that's how "error.leave.balance_insufficient" ends up as the
 * visible UI text on a user's screen). Contract:
 *
 *   - **Dev:** throw synchronously so the first `pnpm dev` / test run that
 *     hits a missing key fails fast. This turns "I forgot to add the Arabic
 *     string" into a stack trace instead of a silent regression.
 *   - **Prod:** log the miss to Sentry via the `globalThis.__linkhrSentry__`
 *     bridge that apps install at boot (see each app's src/main.tsx), then let
 *     i18next's fallback pipeline return the English string. English is the
 *     canonical authoring language and MUST always be 100% complete — the
 *     i18next-parser CI gate (`i18n-keys-sync` job) enforces that.
 *   - **Always:** fall back to English via i18next's own `fallbackLng`
 *     machinery. We return `undefined` from this handler so i18next runs its
 *     normal resolution against `fallbackLng: "en"`.
 *
 * Lazy-namespace load race exception: when a feature namespace (employees /
 * leaves / attendance / …) is still being fetched by `i18next-http-backend`
 * on the very first `t()` call inside that feature, i18next fires a missing
 * key event because the bundle isn't in memory yet — even though the key is
 * present in the JSON that's about to land. We detect that window via
 * `hasResourceBundle()` and skip the dev-throw; once the bundle resolves,
 * the next render returns the real string. Prod Sentry logging stays on for
 * truly-missing keys.
 *
 * We also guard the Sentry call behind a try/catch so a misconfigured
 * Sentry bridge can never break the user's page.
 */
export const missingKeyHandler: MissingKeyHandler = (lngs, ns, key, fallbackValue) => {
  const msg =
    `[i18n] Missing key: ns="${ns}" key="${key}" ` +
    `langs=${JSON.stringify(lngs)} ` +
    `(fallback="${fallbackValue ?? "(none)"}")`;

  // Vite exposes `import.meta.env.DEV` at build time; Vitest sets it true by default.
  const isDev =
    typeof import.meta !== "undefined" &&
    // eslint-disable-next-line @typescript-eslint/no-explicit-any -- Vite's ImportMetaEnv isn't portable across package boundaries
    (import.meta as any).env?.DEV === true;

  // Lazy-namespace load race: if no resource bundle exists for this
  // (lng, ns) pair on ANY of the resolved languages, the namespace is
  // mid-fetch. Don't throw — let i18next's fallback machinery render the
  // fallback string for one render, and the next render (after the bundle
  // resolves) returns the real translation. A truly-missing key (bundle
  // loaded, key absent) still passes this check and surfaces the error.
  const namespaceNotLoaded = lngs.every((lng) => !i18next.hasResourceBundle(lng, ns));
  if (namespaceNotLoaded) {
    return undefined;
  }

  if (isDev) {
    throw new Error(msg);
  }

  try {
    // eslint-disable-next-line @typescript-eslint/no-explicit-any -- Sentry is an optional runtime bridge installed by apps
    const Sentry = (globalThis as any).__linkhrSentry__ as
      | {
          captureMessage?: (
            message: string,
            options?: { level?: string; extra?: Record<string, unknown> },
          ) => void;
        }
      | undefined;
    Sentry?.captureMessage?.(msg, {
      level: "warning",
      extra: { ns, key, langs: lngs },
    });
  } catch {
    /* Sentry bridge misconfigured — never let this crash the page. */
  }

  // Returning undefined lets i18next's built-in fallback resolve against en.
  return undefined;
};
